import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { LogIn, LogOut, Clock } from "lucide-react";

export default function Attendance() {
  const { user } = useAuth();
  const [data, setData] = useState({ history: [], today: null });
  const [allRows, setAllRows] = useState([]);

  const load = async () => {
    const me = await api.get("/attendance/me");
    setData(me.data);
    if (user.role === "admin") {
      const all = await api.get("/attendance/all").catch(() => ({ data: [] }));
      setAllRows(all.data);
    }
  };

  useEffect(() => { load(); }, []);

  const checkin = async () => {
    try { await api.post("/attendance/checkin"); toast.success("Checked in"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const checkout = async () => {
    try { await api.post("/attendance/checkout"); toast.success("Checked out"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const today = data.today;

  return (
    <div className="min-h-screen" data-testid="attendance-page">
      <header className="border-b border-zinc-200 page-px py-6 sm:py-8">
        <div className="label-uppercase mb-2">Workforce</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tighter" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Attendance</h1>
      </header>

      <section className="grid grid-cols-1 lg:grid-cols-3 border-b border-zinc-200">
        <div className="p-6 sm:p-8 lg:border-r border-b lg:border-b-0 border-zinc-200">
          <div className="label-uppercase mb-3">Today · {new Date().toLocaleDateString()}</div>
          <div className="flex items-baseline gap-3 mb-6">
            <Clock size={20} className="text-zinc-400" />
            <div className="text-3xl font-bold font-mono">
              {today?.check_in ? new Date(today.check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—:—"}
            </div>
            <div className="text-sm text-zinc-500">Check-in</div>
          </div>
          <div className="flex items-baseline gap-3 mb-6">
            <Clock size={20} className="text-zinc-400" />
            <div className="text-3xl font-bold font-mono">
              {today?.check_out ? new Date(today.check_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—:—"}
            </div>
            <div className="text-sm text-zinc-500">Check-out</div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={checkin}
              disabled={!!today?.check_in}
              data-testid="checkin-button"
              className="flex-1 bg-ikb text-white py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40"
            >
              <LogIn size={14} /> Check In
            </button>
            <button
              onClick={checkout}
              disabled={!today?.check_in || !!today?.check_out}
              data-testid="checkout-button"
              className="flex-1 bg-zinc-900 text-white py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40"
            >
              <LogOut size={14} /> Check Out
            </button>
          </div>
        </div>

        <div className="lg:col-span-2 p-8">
          <div className="label-uppercase mb-3">My History (last 60 days)</div>
          <div className="border border-zinc-200">
            <div className="grid grid-cols-12 label-uppercase border-b border-zinc-200 px-4 py-2 bg-zinc-50">
              <div className="col-span-4">Date</div>
              <div className="col-span-3">Check-in</div>
              <div className="col-span-3">Check-out</div>
              <div className="col-span-2 text-right">Hours</div>
            </div>
            {data.history.length === 0 && <div className="px-4 py-8 text-sm text-zinc-500">No records.</div>}
            {data.history.map((r) => {
              const hrs = r.check_in && r.check_out
                ? ((new Date(r.check_out) - new Date(r.check_in)) / 3600000).toFixed(2)
                : "—";
              return (
                <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-b border-zinc-200 text-sm">
                  <div className="col-span-4 font-mono">{r.date}</div>
                  <div className="col-span-3 font-mono text-xs">{r.check_in ? new Date(r.check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</div>
                  <div className="col-span-3 font-mono text-xs">{r.check_out ? new Date(r.check_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</div>
                  <div className="col-span-2 text-right font-mono">{hrs}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {user.role === "admin" && (
        <section className="p-6 sm:p-8">
          <h2 className="text-lg font-medium tracking-tight mb-4">Team Attendance</h2>
          <div className="border border-zinc-200">
            <div className="grid grid-cols-12 label-uppercase border-b border-zinc-200 px-4 py-2 bg-zinc-50">
              <div className="col-span-4">Employee</div>
              <div className="col-span-2">Date</div>
              <div className="col-span-3">Check-in</div>
              <div className="col-span-3">Check-out</div>
            </div>
            {allRows.map((r) => (
              <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-b border-zinc-200 text-sm">
                <div className="col-span-4">{r.user_name}</div>
                <div className="col-span-2 font-mono text-xs">{r.date}</div>
                <div className="col-span-3 font-mono text-xs">{r.check_in ? new Date(r.check_in).toLocaleTimeString() : "—"}</div>
                <div className="col-span-3 font-mono text-xs">{r.check_out ? new Date(r.check_out).toLocaleTimeString() : "—"}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
